import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Reveal.css';

const normalizeVariant = (variant) => {
  const value = String(variant || '').trim().toLowerCase();
  if (!value) return 'up';
  if (['up', 'down', 'left', 'right', 'fade', 'scale'].includes(value)) return value;
  return 'up';
};

const Reveal = ({
  as: Tag = 'div',
  children,
  className = '',
  variant = 'up',
  once = true,
  delay = 0,
  duration = 560,
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const variantClass = useMemo(() => `scroll-reveal--${normalizeVariant(variant)}`, [variant]);

  useEffect(() => {
    const node = elementRef.current;
    if (!node) return;

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setIsVisible(true);
          if (once) observer.unobserve(entry.target);
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);

  return (
    <Tag
      ref={elementRef}
      className={[
        'scroll-reveal',
        variantClass,
        isVisible ? 'scroll-reveal--visible' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        '--reveal-delay': `${Math.max(0, Number(delay) || 0)}ms`,
        '--reveal-duration': `${Math.max(0, Number(duration) || 0)}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
